import { Button, Col, Form, Input, Row, Spin, message } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components/macro';

import { ElementDto, TemplateDto } from '../../api/transportLayer';
import { PageTitle } from '../../components/PageTitle';
import { useRootStore } from '../../context/storeContext';
import { UserContainer } from '../Common/styles';

const TemplateEditContainer = styled(UserContainer)`
  & .gd-edit-content {
    padding-top: 20px;
  }

  & .gd-form-container {
    background: ${({ theme: { colors } }) => colors.white};
    border-radius: 12px;
    padding: 32px;
    box-shadow: 0 2px 8px ${({ theme: { colors } }) => colors.black012};
  }

  & .gd-loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
  }

  & .gd-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 24px;
  }
`;

const SectionTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme: { colors } }) => colors.black};
  margin-top: 24px;
  margin-bottom: 16px;
`;

const ElementLabel = styled.div`
  font-weight: 500;
  margin-bottom: 8px;
  color: ${({ theme: { colors } }) => colors.black085};
`;

const ReadOnlyElement = styled.div`
  padding: 12px;
  background: ${({ theme: { colors } }) => colors.lightWhite};
  border-radius: 4px;
  color: ${({ theme: { colors } }) => colors.black06};
  font-size: 14px;
  margin-bottom: 16px;
  border: 1px solid ${({ theme: { colors } }) => colors.black012};

  & img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin-top: 8px;
  }
`;

interface RouteParams {
  templateId: string;
}

export const TemplateEdit = observer(() => {
  const { templatesStore } = useRootStore();
  const history = useHistory();
  const { templateId } = useParams<RouteParams>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState<TemplateDto | null>(null);

  useEffect(() => {
    const loadTemplate = async () => {
      setLoading(true);
      await templatesStore.fetchTemplates();

      const foundTemplate = templatesStore.templates.find(
        (t) => t.id === templateId
      );

      if (foundTemplate) {
        setTemplate(foundTemplate);

        // Set initial form values
        const initialValues: any = {
          title: foundTemplate.title,
        };

        // Add text-type elements to form values
        foundTemplate.elements?.forEach((element, index) => {
          if (element.type === 'text') {
            initialValues[`element_${index}`] = element.value;
          }
        });

        form.setFieldsValue(initialValues);
      }

      setLoading(false);
    };

    loadTemplate();
  }, [templateId, templatesStore, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // Prepare updated template data
      const updatedElements: ElementDto[] =
        template?.elements?.map((element, index) => {
          if (element.type === 'text') {
            return {
              ...element,
              value: values[`element_${index}`] || element.value,
            };
          }
          return element;
        }) || [];

      // Call API to update template
      const result = await templatesStore.updateTemplate(
        template?.id || '',
        values.title,
        updatedElements
      );

      if (result.succeeded) {
        message.success('Template updated successfully');
        // Navigate back to templates list
        history.push('/organization-templates');
      } else {
        message.error(result.message || 'Failed to update template');
      }
    } catch (error) {
      console.error('Validation failed:', error);
      message.error('Please fill in all required fields');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    history.push('/organization-templates');
  };

  if (loading) {
    return (
      <TemplateEditContainer>
        <Col xs={24} sm={24} md={22} lg={20} xl={18} xxl={16}>
          <div className="gd-loading-container">
            <Spin size="large" tip="Loading template..." />
          </div>
        </Col>
      </TemplateEditContainer>
    );
  }

  if (!template) {
    return (
      <TemplateEditContainer>
        <Col xs={24} sm={24} md={22} lg={20} xl={18} xxl={16}>
          <Row>
            <PageTitle title="Template Not Found" />
          </Row>
          <Row className="gd-edit-content">
            <Col span={24}>
              <div className="gd-form-container">
                <p>The requested template could not be found.</p>
                <Button onClick={handleCancel}>Back to Templates</Button>
              </div>
            </Col>
          </Row>
        </Col>
      </TemplateEditContainer>
    );
  }

  return (
    <TemplateEditContainer>
      <Col xs={24} sm={24} md={22} lg={20} xl={18} xxl={16}>
        <Row>
          <PageTitle title="Edit Template" />
        </Row>
        <Row className="gd-edit-content">
          <Col span={24}>
            <div className="gd-form-container">
              <Form form={form} layout="vertical" autoComplete="off">
                <Form.Item
                  label="Template Title"
                  name="title"
                  rules={[
                    { required: true, message: 'Please enter template title' },
                  ]}
                >
                  <Input placeholder="Enter template title" size="large" />
                </Form.Item>

                {template.elements && template.elements.length > 0 && (
                  <>
                    <SectionTitle>Template Elements</SectionTitle>
                    {template.elements.map((element, index) => {
                      // Only show text and image type elements
                      if (element.type !== 'text' && element.type !== 'image') {
                        return null;
                      }

                      return (
                        <div key={index}>
                          {element.type === 'text' ? (
                            <Form.Item
                              label={`Element ${index + 1} - Text`}
                              name={`element_${index}`}
                              rules={[
                                {
                                  required: true,
                                  message: 'Please enter element value',
                                },
                              ]}
                            >
                              <Input.TextArea
                                placeholder="Enter element value"
                                rows={4}
                                size="large"
                              />
                            </Form.Item>
                          ) : (
                            <div style={{ marginBottom: 24 }}>
                              <ElementLabel>
                                Element {index + 1} - Image
                              </ElementLabel>
                              <ReadOnlyElement>
                                {element.value && (
                                  <img
                                    src={element.value}
                                    alt={`Element ${index + 1}`}
                                    onError={(e) => {
                                      (
                                        e.target as HTMLImageElement
                                      ).style.display = 'none';
                                    }}
                                  />
                                )}
                              </ReadOnlyElement>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}

                <div className="gd-actions">
                  <Button size="large" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleSave}
                    loading={saving}
                  >
                    Save Changes
                  </Button>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </Col>
    </TemplateEditContainer>
  );
});
